import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Search from "../../components/form/Search";
import MobileListActionBar from "../../components/mobile-action-bar/MobileListActionBar";
import Pagination from "../../components/Pagination";
import useResponsiveLimit from "../../composables/useResponsiveLimit";
import { useUserStore } from "../../stores/users";
import { formatDateTime } from "../../utils/dateFormat";

function UserRow({ user }) {
  const { t } = useTranslation();

  return (
    <tr>
      <td>
        <Link to={`${user.id}/edit`} className="fw-bold">
          {user.name}
        </Link>
      </td>
      <td>{user.username}</td>
      <td>{user.email}</td>
      <td>{user.number}</td>
      <td>{user.current_login_at ? formatDateTime(user.current_login_at) : "—"}</td>
      <td>{user.last_login_at ? formatDateTime(user.last_login_at) : "—"}</td>
      <td>{user.last_seen ? formatDateTime(user.last_seen) : "—"}</td>
      <td className="text-center">
        {user.active ? <i className="fa fa-check text-success" /> : <i className="fa fa-times text-danger" />}
      </td>
      <td>
        {(user.roles || []).map((role) => (
          <span key={role.id} className="badge bg-default text-dark me-1">
            {t(`users.role_names.${role.name}`, role.name)}
          </span>
        ))}
      </td>
    </tr>
  );
}

function Users() {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useSearchParams();

  const [byIds, allIds, total, fetchSome] = useUserStore(
    (state) => [state.byIds, state.allIds, state.total, state.fetchSome],
    shallow,
  );

  const users = allIds.map((id) => byIds[id]);

  const search = queryParams.get("q") || "";
  const defaultLimit = useResponsiveLimit(10);
  const limit = parseInt(queryParams.get("limit")) || defaultLimit;
  const page = parseInt(queryParams.get("page")) || 0;
  const offset = page * limit;
  const sortBy = queryParams.get("sort_by") || "name";
  const sortDir = queryParams.get("sort_dir") || "asc";

  useEffect(() => {
    fetchSome({ q: search, limit, offset, sort_by: sortBy, sort_dir: sortDir });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, limit, sortBy, sortDir]);

  const handleSearch = (e) => {
    setQueryParams(`page=0&q=${e.target.value}&sort_by=${sortBy}&sort_dir=${sortDir}`);
  };

  const handleSort = (column) => {
    const newDir = sortBy === column && sortDir === "asc" ? "desc" : "asc";
    setQueryParams(`page=0&q=${search}&sort_by=${column}&sort_dir=${newDir}`);
  };

  return (
    <div>
      <Breadcrumb items={[{ label: t("users.users") }]}>
        <Link to="/users/add" className="btn btn-red">
          <i className="fa fa-plus fa-lg me-2 ms-n2"></i> {t("menu.add")}
        </Link>
      </Breadcrumb>

      <MobileListActionBar>
        <Link to="/users/add" className="btn btn-red flex-fill">
          <i className="fa fa-plus fa-fw me-1" />
          {t("menu.add")}
        </Link>
      </MobileListActionBar>

      <div className="card border-0">
        <div className="tab-content p-3">
          <Search handleSearch={handleSearch} search={search} />

          <div className="table-responsive mb-3">
            <table className="table table-hover table-panel text-nowrap align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("name")}>
                    {t("users.name")}
                    {sortBy === "name" && <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("username")}>
                    {t("users.username")}
                    {sortBy === "username" && <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("email")}>
                    {t("users.email")}
                    {sortBy === "email" && <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />}
                  </th>
                  <th width={1} style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("number")}>
                    {t("users.number")}
                    {sortBy === "number" && <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("current_login_at")}>
                    {t("users.current_login_at")}
                    {sortBy === "current_login_at" && (
                      <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />
                    )}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("last_login_at")}>
                    {t("users.last_login_at")}
                    {sortBy === "last_login_at" && (
                      <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />
                    )}
                  </th>
                  <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("last_seen")}>
                    {t("users.last_seen")}
                    {sortBy === "last_seen" && (
                      <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />
                    )}
                  </th>
                  <th width={1} style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("active")}>
                    {t("users.active")}
                    {sortBy === "active" && <i className={`fa fa-sort-${sortDir === "asc" ? "up" : "down"} ms-1`} />}
                  </th>
                  <th>{t("users.roles")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            basePath="/users"
            page={page}
            limit={limit}
            total={total}
            queryParams={queryParams}
            label={t("users.users")}
          />
        </div>
      </div>
    </div>
  );
}

export default Users;
